import React, { Suspense, useEffect, useRef } from 'react';
import { BrowserRouter, Link, Route, Routes, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Home as HomeIcon } from 'lucide-react';
import './i18n';
import './index.css';
import { AuthProvider } from './auth/AuthProvider';
import Layout from './components/layout/Layout';

const Home = React.lazy(() => import('./pages/Home'));
const Reading = React.lazy(() => import('./pages/Reading'));
const Library = React.lazy(() => import('./pages/Library'));
const Journal = React.lazy(() => import('./pages/Journal'));
const Learning = React.lazy(() => import('./pages/Learning'));
const Pricing = React.lazy(() => import('./pages/Pricing'));
const About = React.lazy(() => import('./pages/About'));
const FAQ = React.lazy(() => import('./pages/FAQ'));
const Privacy = React.lazy(() => import('./pages/Privacy'));
const Terms = React.lazy(() => import('./pages/Terms'));
const Contact = React.lazy(() => import('./pages/Contact'));
const Disclaimer = React.lazy(() => import('./pages/Disclaimer'));
const Profile = React.lazy(() => import('./pages/Profile'));
const AdminMemberships = React.lazy(() => import('./pages/AdminMemberships'));

const PageLoading: React.FC = () => {
  const { i18n } = useTranslation();
  const isZh = i18n.language.startsWith('zh');

  return (
    <div className="flex min-h-[65vh] items-center justify-center" role="status" aria-live="polite">
      <div className="text-center">
        <div className="loading-spinner mx-auto" aria-hidden="true" />
        <p className="mt-4 text-sm text-tarot-starLight/55">
          {isZh ? '正在打开页面…' : 'Opening this page…'}
        </p>
      </div>
    </div>
  );
};

const ErrorFallback: React.FC = () => {
  const { i18n } = useTranslation();
  const isZh = i18n.language.startsWith('zh');

  return (
    <div className="flex min-h-screen items-center justify-center bg-tarot-ink px-4">
      <div className="surface-card max-w-lg p-8 text-center sm:p-10">
        <p className="eyebrow">{isZh ? '页面暂时中断' : 'Something interrupted the page'}</p>
        <h1 className="mt-4 text-4xl text-tarot-starLight">
          {isZh ? '让我们回到一个清晰的起点。' : 'Let’s return to a clear starting point.'}
        </h1>
        <p className="mt-4 text-sm leading-7 text-tarot-starLight/60">
          {isZh
            ? '你的私人日志仍保存在此浏览器中。你可以重新加载页面，或返回首页重新开始。'
            : 'Your saved journal remains in this browser. Reload the page, or return home and begin again.'}
        </p>
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <button type="button" onClick={() => window.location.reload()} className="mystical-button">
            {isZh ? '重新加载' : 'Reload page'}
          </button>
          <a
            href="/"
            className="inline-flex min-h-[44px] items-center justify-center rounded-full border border-tarot-gold/40 px-5 py-2 text-sm font-semibold text-tarot-gold transition-colors hover:bg-tarot-gold/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-tarot-gold"
          >
            {isZh ? '返回首页' : 'Return home'}
          </a>
        </div>
      </div>
    </div>
  );
};

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() { return { hasError: true }; }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (process.env.NODE_ENV === 'development') console.error('Application error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback />;
    }
    return this.props.children;
  }
}

const RouteEffects: React.FC = () => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const isZh = i18n.language.startsWith('zh');
  const isInitialRender = useRef(true);

  const routeNames: Record<string, { en: string; zh: string }> = {
    '/': { en: 'Home', zh: '首页' },
    '/reading': { en: 'Reading', zh: '占卜' },
    '/library': { en: 'Card library', zh: '牌库' },
    '/cards': { en: 'Card library', zh: '牌库' },
    '/journal': { en: 'Reading history', zh: '历史解读' },
    '/learning': { en: 'Learning', zh: '学习' },
    '/pricing': { en: 'Pricing', zh: '价格方案' },
    '/account': { en: 'Account', zh: '账户' },
    '/admin/memberships': { en: 'Complimentary access', zh: '赠送权益' },
    '/about': { en: 'About', zh: '关于' },
    '/faq': { en: 'FAQ', zh: '常见问题' },
    '/privacy': { en: 'Privacy', zh: '隐私' },
    '/terms': { en: 'Terms', zh: '条款' },
    '/contact': { en: 'Contact', zh: '联系' },
    '/disclaimer': { en: 'Disclaimer', zh: '免责声明' },
  };
  const currentRoute = routeNames[location.pathname] || { en: 'Not found', zh: '页面未找到' };
  const routeAnnouncement = isZh
    ? `已打开${currentRoute.zh}页面`
    : `${currentRoute.en} page opened`;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });

    if (isInitialRender.current) {
      isInitialRender.current = false;
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      document.getElementById('main-content')?.focus({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname]);

  useEffect(() => {
    document.documentElement.lang = i18n.language.startsWith('zh') ? 'zh-CN' : 'en';
  }, [i18n.language]);

  return (
    <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
      {routeAnnouncement}
    </span>
  );
};

const NotFound: React.FC = () => {
  const { i18n } = useTranslation();
  const isZh = i18n.language.startsWith('zh');
  return (
    <div className="mx-auto flex min-h-[70vh] max-w-2xl items-center px-4 py-20 text-center">
      <div className="w-full">
        <p className="eyebrow">404</p>
        <h1 className="mt-5 text-5xl text-tarot-starLight">{isZh ? '这条路径没有对应的牌面。' : 'There is no card for this path.'}</h1>
        <p className="mx-auto mt-5 max-w-lg text-base leading-8 text-tarot-starLight/60">{isZh ? '页面可能已移动。回到首页，或直接开始一次新的解读。' : 'The page may have moved. Return home, or begin a fresh reading.'}</p>
        <Link to="/" className="mystical-button mt-8"><HomeIcon className="h-4 w-4" />{isZh ? '返回首页' : 'Return home'}</Link>
      </div>
    </div>
  );
};

const App: React.FC = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <RouteEffects />
          <Layout>
            <Suspense fallback={<PageLoading />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/reading" element={<Reading />} />
                <Route path="/library" element={<Library />} />
                <Route path="/cards" element={<Library />} />
                <Route path="/journal" element={<Journal />} />
                <Route path="/learning" element={<Learning />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/account" element={<Profile />} />
                <Route path="/admin/memberships" element={<AdminMemberships />} />
                <Route path="/about" element={<About />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/disclaimer" element={<Disclaimer />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </Layout>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;
